import { AlertTriangle, Camera, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PHOTO_TIPS, type PhotoQuality } from "@/lib/quality";
import { useLocale } from "@/lib/i18n/locale-context";

type Props = {
  quality: PhotoQuality;
  /** De bilder som AI:n faktiskt har markerat. */
  flaggedImages?: { index: number; src: string }[];
  onRetake: (index: number) => void;
  /** Visas bara när prylen syns men bakgrunden är rörig. */
  onCleanBackground?: () => void;
  /** Finns bara vid "unusable" – låter säljaren köra vidare ändå. */
  onContinueAnyway?: () => void;
  onDismiss?: () => void;
};

/**
 * Visas när fotot inte duger som annonsbild. Gul vid svag bild (annonsen är
 * ändå skriven), röd vid oanvändbar bild (då har vi inte fyllt i något).
 */
export function PhotoQualityNotice({
  quality,
  flaggedImages = [],
  onRetake,
  onCleanBackground,
  onContinueAnyway,
  onDismiss,
}: Props) {
  const { lang } = useLocale();
  const t = lang === "es" ? { bad: "Hay que repetir la foto", improve: "La foto se puede mejorar", unclear: "El objeto no se ve con suficiente claridad para redactar un anuncio fiable.", better: "El anuncio está redactado, pero una mejor foto atraerá a más personas.", image: "Foto", retakeText: "debe repetirse", retake: "Repetir", retakeImage: "Repetir foto", clean: "Limpiar fondo", continue: "Usar la foto y redactar el anuncio igualmente", hide: "Ocultar" }
    : lang === "en" ? { bad: "The photo needs retaking", improve: "The photo can be improved", unclear: "The item is not clear enough for us to write a reliable listing.", better: "The listing is ready, but a better photo will attract more buyers.", image: "Image", retakeText: "needs retaking", retake: "Retake", retakeImage: "Retake photo", clean: "Clean background", continue: "Use the photo and write the listing anyway", hide: "Hide" }
      : { bad: "Bilden behöver tas om", improve: "Bilden går att förbättra", unclear: "Prylen syns inte tillräckligt tydligt för att vi ska kunna skriva en säker annonstext.", better: "Annonsen är skriven, men en bättre bild ger fler intresserade.", image: "Bild", retakeText: "behöver tas om", retake: "Ta om", retakeImage: "Ta om bilden", clean: "Rensa bakgrund", continue: "Använd bilden ändå och skriv annonstexten", hide: "Dölj" };
  const tips = lang === "es" ? ["Un objeto por anuncio; no mezcles cosas diferentes.", "Coloca el objeto en el centro y muéstralo entero.", "Fotografía con luz natural y un fondo tranquilo.", "Añade un primer plano del desgaste o los daños."]
    : lang === "en" ? ["One item per listing—do not mix unrelated things.", "Centre the item and include the whole object.", "Photograph in daylight against a calm background.", "Include a close-up of wear or damage."]
      : PHOTO_TIPS;
  const blocking = quality.verdict === "unusable";
  const issues = quality.issues.filter(Boolean).slice(0, 3);
  const summary = issues[0] || quality.advice;

  return (
    <div
      role="alert"
      className={`rounded-xl border p-4 ${
        blocking
          ? "border-destructive/60 bg-destructive/10"
          : "border-amber-400/60 bg-amber-400/10"
      }`}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle
          className={`mt-0.5 h-5 w-5 shrink-0 ${blocking ? "text-destructive" : "text-amber-400"}`}
        />
        <div className="min-w-0 flex-1">
          <p className="font-heading text-sm font-bold text-white">
             {blocking ? t.bad : t.improve}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-ocean-100/90">
            {blocking
               ? summary || t.unclear
               : t.better}
          </p>

          {issues.length > (blocking && summary === issues[0] ? 1 : 0) && (
            <ul className="mt-2 space-y-1 text-xs text-ocean-100/90">
              {issues.slice(blocking && summary === issues[0] ? 1 : 0).map((issue) => (
                <li key={issue} className="flex gap-2">
                  <span className="text-ocean-300">•</span>
                  <span>{issue}</span>
                </li>
              ))}
            </ul>
          )}

          {quality.advice && quality.advice !== summary && (
            <p className="mt-2 text-xs font-medium text-ocean-100">{quality.advice}</p>
          )}

          {flaggedImages.length > 0 && (
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {flaggedImages.slice(0, 4).map(({ index, src }) => (
                <figure key={index} className="overflow-hidden rounded-md border border-current/20 bg-background/10">
                  <div className="flex aspect-[4/3] items-center justify-center bg-background/20 p-2">
                    <img
                      src={src}
                       alt={`${t.image} ${index + 1} ${t.retakeText}`}
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <figcaption className="flex items-center justify-between gap-3 p-3">
                     <span className="text-xs font-semibold">{t.image} {index + 1} {t.retakeText}</span>
                    <Button type="button" size="sm" onClick={() => onRetake(index)} className="shrink-0 gap-1.5">
                      <Camera className="h-4 w-4" />
                       {t.retake}
                    </Button>
                  </figcaption>
                </figure>
              ))}
            </div>
          )}

          {blocking && (
            <ul className="mt-3 space-y-1 text-[11px] leading-relaxed text-ocean-100/80">
             {tips.map((tip) => (
                <li key={tip} className="flex gap-2">
                  <span className="text-ocean-300">•</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {flaggedImages.length === 0 && <Button type="button" size="sm" onClick={() => onRetake(quality.imageIndexes[0] ?? 0)} className="gap-1.5">
              <Camera className="h-4 w-4" />
               {t.retakeImage}
            </Button>}
            {quality.clutter && onCleanBackground && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={onCleanBackground}
                className="gap-1.5"
              >
                <Wand2 className="h-4 w-4" />
                 {t.clean}
              </Button>
            )}
            {blocking && onContinueAnyway && (
              <button
                type="button"
                onClick={onContinueAnyway}
                className="text-xs font-medium text-ocean-100/80 underline underline-offset-2 hover:text-white"
              >
                 {t.continue}
              </button>
            )}
            {!blocking && onDismiss && (
              <button
                type="button"
                onClick={onDismiss}
                className="text-xs font-medium text-ocean-100/80 underline underline-offset-2 hover:text-white"
              >
                 {t.hide}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
